import { AnalyticsContext } from '@carvana/eventable-clickstream';
import { datadogRum } from '@datadog/browser-rum';
import React, {
  PropsWithChildren,
  Suspense,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import styled from 'styled-components';
import BaseButton from '|/components/base/button';
import FormError from '|/components/base/form-error';
import Loading from '|/components/base/loading';
import LoadingIcon from '|/components/base/loading-icon';
import accessToken from '|/helpers/access-token';
import ClickBlockingContext from '../../app/click-blocking-overlay';
import DevicePlatformContext from '../../app/device-platform-context';
import ModalContext from '../../app/modals';
import sendNativeEvent from '../send-native-event';
import useNavigateToNextStep from '../use-navigate-to-next-step';

const ChildrenContainer = styled.div`
  &.hidden {
    display: none;
  }
`;

const defaultState = {
  load: <T extends any = never>(
    _: Promise<T>,
    _signal?: AbortSignal,
    _ignoreEvent?: boolean
  ) => {},
  submit: <T extends any = never>(
    _: Promise<T>,
    _signal?: AbortSignal | null,
    _nextStepOnSucces?: boolean
  ) => {},
  error: null as null | Error,
  isSubmitting: false
};

const AppFrameworkContext = createContext(defaultState);

const BaseApplicationShell: React.FC<React.PropsWithChildren<{
  isLoading: boolean;
  loadError: Error | null;
}>> = ({ isLoading, loadError, children }) => {
  if (loadError !== null) {
    throw loadError;
  }
  return (
    <Suspense fallback={<Loading />}>
      {isLoading && <Loading />}
      <ChildrenContainer className={isLoading ? 'hidden' : ''}>
        {children}
      </ChildrenContainer>
    </Suspense>
  );
};

const usePageState = () => {
  const { setShouldBlock } = useContext(ClickBlockingContext);
  const navigateToNextStep = useNavigateToNextStep();
  const [loadingCount, setLoadingCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null as null | Error);
  const [submitError, setSubmitError] = useState(null as null | Error);

  const load = useCallback(
    <T extends any = never>(promise: Promise<T>, signal?: AbortSignal) => {
      setLoadingCount(count => count + 1);
      return promise
        .catch(errorFromLoad => {
          if (signal?.aborted) {
            return;
          }
          setLoadError(errorFromLoad);
          throw errorFromLoad;
        })
        .finally(() => {
          setLoadingCount(count => count - 1);
        });
    },
    [setLoadingCount, setLoadError]
  );

  const submit = useCallback(
    <T extends any = never>(
      promise: Promise<T>,
      signal: AbortSignal | null = null,
      nextStepOnSuccess = true
    ) => {
      setIsSubmitting(true);
      setSubmitError(null);
      setShouldBlock(true);
      return promise
        .then(value => {
          if (nextStepOnSuccess) navigateToNextStep();
          setIsSubmitting(false);
          return value;
        })
        .catch(errorFromSubmit => {
          if (signal?.aborted) {
            return;
          }
          const errorToSend =
            errorFromSubmit instanceof Error
              ? errorFromSubmit
              : new Error(errorFromSubmit);
          datadogRum.addError(errorToSend, {
            customType: 'pageSubmit'
          });
          setSubmitError(errorFromSubmit);
          setIsSubmitting(false);
        })
        .finally(() => setShouldBlock(false));
    },
    [setIsSubmitting, setSubmitError, navigateToNextStep, setShouldBlock]
  );

  const isLoading = loadingCount > 0;

  return { load, submit, loadError, submitError, isLoading, isSubmitting };
};

const usePageStateWithEventing = (
  loadEventName: string,
  submitEventName: string,
  extraContext: Record<string, string> | null = null
) => {
  const { trackEvent } = useContext(AnalyticsContext);
  const [loadingCount, setLoadingCount] = useState(0);
  const prevLoadingCountRef = useRef(0);
  const {
    load: baseLoad,
    submit: baseSubmit,
    loadError,
    submitError,
    isLoading,
    isSubmitting
  } = usePageState();
  const devicePlatform = useContext(DevicePlatformContext);

  useEffect(() => {
    if (loadingCount === 0 && prevLoadingCountRef.current > 0) {
      sendNativeEvent({
        event: 'page_ready',
        source: 'purchase'
      });
    }
    prevLoadingCountRef.current = loadingCount;
  }, [loadingCount]);

  const url = window.location.pathname;

  const sendEvent = useCallback(
    (eventName: string, success: boolean, duration: number, data: any) => {
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id
      };
      const metadata = {
        ...(data || {}),
        ...(extraContext || {}),
        ...attributesToSend
      };
      trackEvent(eventName, {
        url,
        userId: accessToken?.().user.user_id,
        successEvent: success,
        duration,
        metadata,
        devicePlatform
      });
    },
    [trackEvent, url, extraContext, devicePlatform]
  );

  const sendNoFlagEvent = useCallback(
    (eventName: string, duration: number, data: any) => {
      const ddContext = datadogRum.getInternalContext();
      const attributesToSend = {
        datadogSessionId: ddContext?.session_id,
        datadogViewId: ddContext?.view?.id
      };
      const metadata = {
        ...(data || {}),
        ...(extraContext || {}),
        ...attributesToSend
      };
      trackEvent(eventName, {
        url,
        userId: accessToken?.().user.user_id,
        duration,
        metadata,
        devicePlatform
      });
    },
    [trackEvent, url, extraContext, devicePlatform]
  );

  const sendLoadEvent = useCallback(
    (success: boolean, duration: number, data: any) => {
      sendEvent(loadEventName, success, duration, data);
    },
    [sendEvent, loadEventName]
  );

  const sendSubmitEvent = useCallback(
    (success: boolean, duration: number, data: any) => {
      sendEvent(submitEventName, success, duration, data);
    },
    [sendEvent, submitEventName]
  );

  const load = useCallback(
    <T extends any = never>(
      promise: Promise<T>,
      signal?: AbortSignal,
      ignoreEvent?: boolean
    ) => {
      const startTime = window.performance.now();
      setLoadingCount(count => count + 1);
      return baseLoad(promise)
        .then(data => {
          const endTime = window.performance.now();
          const duration = endTime - startTime;
          if (!ignoreEvent) sendLoadEvent(true, duration, data);
          return data;
        })
        .catch(errorFromLoad => {
          if (signal?.aborted) {
            return;
          }
          const endTime = window.performance.now();
          const duration = endTime - startTime;
          const errorToSend =
            errorFromLoad instanceof Error
              ? errorFromLoad
              : new Error(errorFromLoad);
          datadogRum.addError(errorToSend, {
            customType: 'pageLoad'
          });
          sendLoadEvent(false, duration, errorFromLoad);
          sendNoFlagEvent(`${loadEventName}_error`, duration, errorFromLoad);
          throw errorFromLoad;
        })
        .finally(() => {
          setLoadingCount(count => count - 1);
        });
    },
    [sendLoadEvent, baseLoad, sendNoFlagEvent, loadEventName]
  );

  const submit = useCallback(
    <T extends any = never>(
      promise: Promise<T>,
      signal: AbortSignal | null = null,
      nextStepOnSuccess = true
    ) => {
      const startTime = window.performance.now();
      return baseSubmit(promise, signal, nextStepOnSuccess)
        .then(value => {
          const endTime = window.performance.now();
          const duration = endTime - startTime;
          sendSubmitEvent(true, duration, value);
          return value;
        })
        .catch(errorFromSubmit => {
          if (signal?.aborted) {
            return;
          }
          const endTime = window.performance.now();
          const duration = endTime - startTime;
          const errorToSend =
            errorFromSubmit instanceof Error
              ? errorFromSubmit
              : new Error(errorFromSubmit);
          datadogRum.addError(errorToSend, {
            customType: 'pageSubmit'
          });
          sendSubmitEvent(false, duration, errorFromSubmit);
          sendNoFlagEvent(
            `${submitEventName}_error`,
            duration,
            errorFromSubmit
          );
        });
    },
    [
      sendSubmitEvent,
      baseSubmit,
      submitEventName,
      sendNoFlagEvent
    ]
  );

  return { load, submit, loadError, submitError, isLoading, isSubmitting };
};

const SimpleAppFrameworkContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const {
    load,
    submit,
    isLoading,
    isSubmitting,
    loadError,
    submitError
  } = usePageState();

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error: submitError, isSubmitting }}
      >
        <BaseApplicationShell isLoading={isLoading} loadError={loadError}>
          {children}
        </BaseApplicationShell>
      </AppFrameworkContext.Provider>
    </>
  );
};

const AppFrameworkContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const {
    load,
    submit,
    isLoading,
    isSubmitting,
    loadError,
    submitError
  } = usePageStateWithEventing('pp_load', 'pp_complete');

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error: submitError, isSubmitting }}
      >
        <BaseApplicationShell isLoading={isLoading} loadError={loadError}>
          {children}
        </BaseApplicationShell>
      </AppFrameworkContext.Provider>
    </>
  );
};

const ModalFrameworkContextProvider: React.FC<PropsWithChildren<{}>> = ({
  children
}) => {
  const { currentModal } = useContext(ModalContext);
  const extraContext = useMemo<Record<string, string>>(() => {
    return { modalName: currentModal || '' };
  }, [currentModal]);
  const {
    load,
    submit: baseSubmit,
    isLoading,
    isSubmitting,
    loadError,
    submitError
  } = usePageStateWithEventing(
    'pp_modal_load',
    'pp_modal_complete',
    extraContext
  );

  const submit = useCallback(
    <T,>(
      promise: Promise<void | T>,
      signal?: AbortSignal | null,
      _?: boolean
    ) => baseSubmit(promise, signal, false),
    [baseSubmit]
  );

  return (
    <>
      <AppFrameworkContext.Provider
        value={{ load, submit, error: submitError, isSubmitting }}
      >
        <BaseApplicationShell isLoading={isLoading} loadError={loadError}>
          {children}
        </BaseApplicationShell>
      </AppFrameworkContext.Provider>
    </>
  );
};

type AppFrameworkButtonProps = {
  isPrimaryCta?: boolean;
};

const Button: React.FC<
  React.ComponentProps<typeof BaseButton> & AppFrameworkButtonProps
> = props => {
  const { isPrimaryCta = false, ...rest } = props;
  const { disabled } = rest;
  const { error, isSubmitting } = useContext(AppFrameworkContext);
  return (
    <>
      {isPrimaryCta && error && (
        <FormError>There was an unexpected error.</FormError>
      )}
      {isPrimaryCta && isSubmitting && <LoadingIcon />}
      {(!isPrimaryCta || !isSubmitting) && (
        <BaseButton
          {...rest}
          disabled={(!isPrimaryCta && isSubmitting) || disabled}
        />
      )}
    </>
  );
};

export default AppFrameworkContext;
export {
  AppFrameworkContextProvider,
  Button,
  ModalFrameworkContextProvider,
  SimpleAppFrameworkContextProvider
};
